import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';

export interface ReplacementGarmentInstructions {
  intentId: string;
  digest: string;
  orderNumber: string;
  replacementTitle: string;
  variantLabel: string;
  quantity: number;
  heightLabel: string;
  heightValueLabel: string;
}
interface Props {
  instructionState: 'PREPARING' | 'VERIFIED' | 'REVIEW_REQUIRED';
  scopeKey: string;
  instructions: ReplacementGarmentInstructions;
  canAcknowledge: boolean;
  acknowledgedDigest: string | null;
  pending: boolean;
  onAcknowledge: (input: {
    intentId: string;
    expectedDigest: string;
  }) => Promise<boolean>;
  onReload: () => Promise<boolean>;
}
interface State {
  scope: object;
  checked: boolean;
  busy: boolean;
  awaiting: boolean;
  error: string | null;
}
const text = (value: unknown, max = 120) =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= max &&
  !/[\p{Cc}\p{Cf}]/u.test(value);
const displayTitle = (value: unknown) =>
  typeof value === 'string' &&
  value.length <= 300 &&
  text(value.replace(/[\u200c\u200d]/g, ''), 300);
function valid(value: ReplacementGarmentInstructions) {
  return (
    value &&
    /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value.intentId) &&
    /^[a-f0-9]{64}$/.test(value.digest) &&
    [
      value.orderNumber,
      value.heightLabel,
      value.heightValueLabel,
    ].every((value) => text(value, 300)) &&
    displayTitle(value.replacementTitle) &&
    text(value.variantLabel, 1000) &&
    Number.isSafeInteger(value.quantity) &&
    value.quantity > 0 &&
    value.quantity <= 100
  );
}
/** Presentation receives only a separately verified instruction/acknowledgment projection. */
export default function ReturnCustomizationInstructions(props: Props) {
  const key = JSON.stringify([
      props.scopeKey,
      props.instructions,
      props.instructionState,
    ]),
    owner = useRef({ key }),
    mounted = useRef(true),
    operation = useRef<object | null>(null);
  if (owner.current.key !== key) {
    owner.current = { key };
    operation.current = null;
  }
  const scope = owner.current,
    initial: State = {
      scope,
      checked: false,
      busy: false,
      awaiting: false,
      error: null,
    };
  const [state, setState] = useState<State>(initial),
    current = state.scope === scope ? state : initial;
  const latest = useRef(current);
  latest.current = current;
  const latestProps = useRef(props);
  latestProps.current = props;
  const live = () => mounted.current && owner.current === scope;
  const update = (next: State) => {
    if (live()) {
      latest.current = next;
      setState(next);
    }
  };
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const verified = valid(props.instructions),
    instructionsReady = props.instructionState === 'VERIFIED',
    acknowledged =
      verified &&
      instructionsReady &&
      props.acknowledgedDigest === props.instructions.digest;
  async function acknowledge() {
    if (
      !live() ||
      operation.current ||
      !verified ||
      latestProps.current.instructionState !== 'VERIFIED' ||
      !latest.current.checked ||
      latest.current.awaiting ||
      latestProps.current.pending ||
      !latestProps.current.canAcknowledge ||
      latestProps.current.acknowledgedDigest === props.instructions.digest
    )
      return;
    const request = {};
    operation.current = request;
    update({ ...latest.current, busy: true, error: null });
    const owns = () => live() && operation.current === request;
    try {
      const accepted = await props.onAcknowledge({
        intentId: props.instructions.intentId,
        expectedDigest: props.instructions.digest,
      });
      if (owns())
        update({
          ...latest.current,
          busy: false,
          awaiting: true,
          error: accepted
            ? 'Check the latest instructions to verify acknowledgment.'
            : 'Instruction acknowledgment could not be verified. Check the latest instructions before trying again.',
        });
    } catch {
      if (owns())
        update({
          ...latest.current,
          busy: false,
          awaiting: true,
          error:
            'Instruction acknowledgment could not be verified. Check the latest instructions before trying again.',
        });
    } finally {
      if (owns()) operation.current = null;
    }
  }
  async function reload() {
    if (!live() || operation.current || latestProps.current.pending) return;
    const request = {};
    operation.current = request;
    update({ ...latest.current, busy: true });
    const owns = () => live() && operation.current === request;
    try {
      const accepted = await props.onReload();
      if (owns())
        update({
          ...latest.current,
          busy: false,
          checked: false,
          awaiting: !accepted,
          error: accepted
            ? null
            : 'Instruction acknowledgment could not be verified. Check the latest instructions before trying again.',
        });
    } catch {
      if (owns())
        update({
          ...latest.current,
          busy: false,
          awaiting: true,
          error:
            'Instruction acknowledgment could not be verified. Check the latest instructions before trying again.',
        });
    } finally {
      if (owns()) operation.current = null;
    }
  }
  return (
    <section
      aria-label="Replacement production instructions"
      className="space-y-3 rounded-xl border border-gray-200 bg-white p-4"
    >
      <h3 className="text-sm font-semibold text-gray-900">
        Replacement instructions
      </h3>
      {!verified ? (
        <p role="alert" className="text-sm text-amber-800">
          Verified replacement instructions are unavailable. Check the latest
          status before processing.
        </p>
      ) : (
        <>
          {props.instructionState === 'PREPARING' && (
            <p role="status" className="text-sm text-gray-600">
              Preparing instructions on the original Shopify order…
            </p>
          )}
          {props.instructionState === 'REVIEW_REQUIRED' && (
            <p role="alert" className="text-sm text-amber-800">
              Verified original-order instructions need a team review before
              acknowledgment.
            </p>
          )}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            {!instructionsReady && (
              <p className="mb-1 text-xs font-medium text-gray-500">
                Proposed replacement
              </p>
            )}
            <p className="font-semibold text-gray-900">
              {props.instructions.replacementTitle}
            </p>
            <p className="break-words">{props.instructions.variantLabel}</p>
            <p>
              {props.instructions.heightLabel}:{' '}
              {props.instructions.heightValueLabel}
            </p>
            <p>Quantity: {props.instructions.quantity}</p>
            {instructionsReady && (
              <p className="mt-2 text-xs">
                Use the verified instructions on original Shopify order{' '}
                {props.instructions.orderNumber} for all{' '}
                {props.instructions.quantity} replacement{' '}
                {props.instructions.quantity === 1 ? 'item' : 'items'}.
              </p>
            )}
          </div>
          <p className="text-xs leading-5 text-gray-600">
            This confirms production instructions only. Request approval and
            return inspection remain separate.
          </p>
          {acknowledged ? (
            <p role="status" className="text-sm font-medium text-green-700">
              Replacement instructions acknowledged.
            </p>
          ) : (
            <>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={current.checked}
                  disabled={
                    current.busy ||
                    current.awaiting ||
                    props.pending ||
                    !instructionsReady ||
                    !props.canAcknowledge
                  }
                  onChange={(event) => {
                    if (
                      live() &&
                      !operation.current &&
                      !latest.current.awaiting &&
                      !latestProps.current.pending &&
                      latestProps.current.instructionState === 'VERIFIED' &&
                      latestProps.current.canAcknowledge
                    )
                      update({
                        ...latest.current,
                        checked: event.target.checked,
                      });
                  }}
                />
                <span>
                  I confirm our team will use these exact instructions for all{' '}
                  {props.instructions.quantity} replacement{' '}
                  {props.instructions.quantity === 1 ? 'item' : 'items'} before
                  fulfillment.
                </span>
              </label>
              {!props.canAcknowledge && instructionsReady && (
                <p className="text-xs text-amber-800">
                  Instructions are not ready for acknowledgment. Check the
                  latest status.
                </p>
              )}
              <Button
                disabled={
                  !current.checked ||
                  current.busy ||
                  current.awaiting ||
                  props.pending ||
                  !instructionsReady ||
                  !props.canAcknowledge
                }
                isLoading={current.busy}
                onClick={acknowledge}
              >
                Confirm replacement instructions
              </Button>
            </>
          )}
          {current.error && !acknowledged && (
            <p role="alert" className="text-sm text-amber-800">
              {current.error}
            </p>
          )}
        </>
      )}
      <Button
        variant="outlined"
        disabled={current.busy || props.pending}
        onClick={reload}
      >
        Check latest instructions
      </Button>
    </section>
  );
}
